import React, { useRef, Suspense, memo, useState } from "react";
import {
  HStack,
  Box,
  Input,
  Text,
  FormControl,
  Icon,
  InputGroup,
  InputLeftElement,
  FormErrorMessage,
} from "@chakra-ui/react";
import { Field, useField, useFormikContext } from "formik";
import { IoImages } from "react-icons/io5";
import { useIntl } from "react-intl";
import CaptureOnMobileButton from "components/CaptureOnMobileButton";
import { urlToFile, getIsMobile, getDimensionsFromImgFile, useSessionJWT } from "utils";
import { logError } from "api/accounts-customer";

import FileUploadPreview from "./FileUploadPreview";
import {
  Loading,
  Passport,
  DriversLicenseFront,
  DriversLicenseBack,
  IdentityCardFront,
  IdentityCardBack,
  ResidencePermitFront,
  ResidencePermitBack,
} from "../graphics";
import { DOCUMENTS_FILETYPES } from "../utils";

const FileUploadInput = ({ name }) => {
  const { formatMessage } = useIntl();
  const [field, _meta, { setValue }] = useField(name);
  const { sessionJWT } = useSessionJWT();
  const inputRef = useRef();
  const [uploadError, setUploadError] = useState(null);
  // While the file is being read/decoded show a pending state so a slow
  // network/cloud-drive read doesn't itself read as "nothing happens".
  const [isReading, setIsReading] = useState(false);

  // Report through the widget's standard error path (logError -> /ui/api/log/error),
  // matching every other failure site (Paypal/Button.jsx, BankAccountForm, etc.).
  // PII: this is a KYC flow — never log file.name or the file path. `meta` carries
  // only non-sensitive file metadata (type, size). logError is fire-and-forget.
  const logKycUploadError = (reason, error, meta = {}) =>
    logError(sessionJWT, {
      flowLoc: "KYC.DOCUMENT_UPLOAD",
      reason,
      message: error?.message,
      data: { errorName: error?.name, ...meta },
    });

  const clearInput = (fileInputEl) => {
    if (fileInputEl && fileInputEl.value) {
      try {
        fileInputEl.value = ""; // let the user re-select the same file
      } catch {
        // no-op, just an edge case we don't really have to handle as the user needs to select a different file
      }
    }
  };

  const validateMinImgSizeAndSet = async (file, fileInputEl) => {
    let dim;
    try {
      dim = await getDimensionsFromImgFile(file);
    } catch (error) {
      // Read/decode failed — e.g. a file on a network/cloud drive that errored,
      // an unreadable/locked file, or an image the browser can't decode. This
      // also covers a stalled read where neither onload nor onerror fires:
      // getDimensionsFromImgFile rejects on a timeout so we reach this catch,
      // clear isReading, and re-enable the picker rather than spinning forever.
      logKycUploadError("document_read_failed", error, {
        fileType: file?.type,
        fileSize: file?.size,
      });
      setUploadError(
        formatMessage({
          defaultMessage:
            "We couldn’t open that file. If it’s on a network or cloud drive, copy it to this device first, then select a JPG or PNG.",
          description: "Upload error shown below the file picker",
        })
      );
      clearInput(fileInputEl);
      return;
    }

    if (dim.width >= 450 && dim.height >= 600) {
      setUploadError(null); // clear any previous error on success
      setValue(file);
    } else {
      setUploadError(
        formatMessage({
          defaultMessage: "Please select an image at least 450 × 600 pixels.",
          description: "Upload error shown below the file picker",
        })
      );
      clearInput(fileInputEl);
    }
  };

  const handleOnChange = (event) => {
    const fileInputEl = event.currentTarget;
    const f = fileInputEl.files[0];
    if (f && f.type && f.type.includes("image/")) {
      setIsReading(true);
      // Never leave this floating: surface anything the inner handler misses.
      validateMinImgSizeAndSet(f, fileInputEl)
        .catch((error) => {
          logKycUploadError("document_change_handler_failed", error, {
            fileType: f?.type,
            fileSize: f?.size,
          });
          setUploadError(
            formatMessage({
              defaultMessage: "We couldn’t open that file. Please try a different JPG or PNG.",
              description: "Upload error shown below the file picker",
            })
          );
          clearInput(fileInputEl);
        })
        .finally(() => setIsReading(false));
    } else {
      /**
       * This will only fire if the user changes which file they've selected
       * as <input type="file" /> is fully user controlled and the "change" event
       * won't fire if the file the user selected hasn't changed.
       */
      setUploadError(
        formatMessage({
          defaultMessage: "Please select a jpg or a png image file.",
          description: "Upload error shown below the file picker",
        })
      );
      clearInput(fileInputEl);
    }
  };

  return (
    <Box flex={1}>
      <InputGroup flex={1}>
        <InputLeftElement pointerEvents="none">
          <Icon as={IoImages} />
        </InputLeftElement>
        <input
          type="file"
          accept={DOCUMENTS_FILETYPES}
          name={name}
          ref={inputRef}
          onChange={handleOnChange}
          style={{ display: "none" }}
          data-testid="document-upload-hidden-input"
        />
        <Input
          variant="outline"
          cursor="pointer"
          fontWeight={600}
          isDisabled={isReading}
          placeholder={formatMessage({
            defaultMessage: "Pick a file",
            description: "Placeholder text",
          })}
          _placeholder={{
            color: "primaryText",
            fontWeight: 600,
          }}
          onClick={() => inputRef.current.click()}
          readOnly={true}
          value={field?.value?.name || ""}
          aria-label={formatMessage({
            defaultMessage: "Pick a file",
            description: "Placeholder text",
          })}
        />
      </InputGroup>
      {isReading && (
        <Text mt={2} fontSize="sm" color="secondaryText" data-testid="document-upload-reading">
          {formatMessage({
            defaultMessage: "Reading file…",
            description: "Shown while an upload is being read/decoded",
          })}
        </Text>
      )}
      {uploadError && (
        <Text
          as="i"
          role="alert"
          mt={2}
          fontSize="sm"
          color="errorText"
          data-testid="document-upload-error"
        >
          {uploadError}
        </Text>
      )}
    </Box>
  );
};

/**
 * CaptureOnMobileWrapper
 * The wrapped CaptureOnMobileButton button will capture documents based on the
 * docTypeConfigKey key that is supplied to it.  It will pull the associated values from
 * "src/components/CaptureOnMobileButton/uploadConfigs.js".  Make sure the key values
 * the form is expecting matches the refId values in that configuration.
 * Since opening the 'Capture on mobile' button is meant to open a page where both
 * a front and back image can be supplied, this wrapper component sets the values
 * directly on the form based on those reference IDs.
 */
const CaptureOnMobileWrapper = ({ documentType }) => {
  const { setFieldValue } = useFormikContext();

  const handleUploadFromMobile = async (uploads) => {
    if (!uploads || Object.keys(uploads).length === 0) {
      // shouldn't ever be the case, but you know, no typescript
      return;
    }
    Object.keys(uploads).forEach(async (key) => {
      const val = uploads[key];
      const mimeTypeMatch = (val || "").match(/data:(.*);base64/);
      if (mimeTypeMatch && mimeTypeMatch.length > 1) {
        const mimeType = mimeTypeMatch[1];
        const ext = mimeType.split("/")[1];
        const file = await urlToFile(val, `${Date.now()}.${ext}`, mimeTypeMatch[1]);
        setFieldValue(key, file); // if key is not already in the context, this will fail silently
      }
    });
  };

  return (
    <CaptureOnMobileButton docTypeConfigKey={documentType} onComplete={handleUploadFromMobile} />
  );
};

const documentTypeToGraphics = {
  PP: [Passport],
  IC: [IdentityCardFront, IdentityCardBack],
  RP: [ResidencePermitFront, ResidencePermitBack],
  DL: [DriversLicenseFront, DriversLicenseBack],
};

function DocumentUploadFields() {
  const [isMobile] = useState(getIsMobile());
  const [documentType] = useField("documents.document_type");

  return documentTypeToGraphics[documentType.value].map((Graphic, i) => {
    const name = `documents.${i === 0 ? "document_front" : "document_back"}`;

    return (
      <Field name={name} key={`document-upload-field-${i}`}>
        {({ field, meta }) => (
          <FormControl
            isInvalid={meta.touched && meta.error}
            flex={1}
            key={`document-upload-field-${i}`}
          >
            <Box>
              <Box px={2}>
                {field.value ? (
                  <FileUploadPreview name={name} />
                ) : (
                  <Suspense fallback={<Loading />}>
                    <Graphic />
                  </Suspense>
                )}
              </Box>

              <HStack spacing={1} mt={4} alignItems="flex-start">
                {isMobile ? null : <CaptureOnMobileWrapper documentType={documentType.value} />}
                <FileUploadInput name={name} />
              </HStack>

              <FormErrorMessage>{meta.error}</FormErrorMessage>
            </Box>
          </FormControl>
        )}
      </Field>
    );
  });
}

export default memo(DocumentUploadFields);
